import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './contexts/AuthContext';
import { TicketProvider } from './contexts/TicketContext';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/layout/ProtectedRoute';
import { useTranslation } from 'react-i18next';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import TicketUploadPage from './pages/ticket/TicketUploadPage';
import TicketReviewPage from './pages/ticket/TicketReviewPage';
import TicketEvidencePage from './pages/ticket/TicketEvidencePage';
import AppealSummaryPage from './pages/ticket/AppealSummaryPage';
import TicketFilingGuidePage from './pages/ticket/TicketFilingGuidePage';
import AdminCasesPage from './pages/admin/AdminCasesPage';
import AdminCaseDetailPage from './pages/admin/AdminCaseDetailPage';
import SubscribePage from './pages/SubscribePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';

// Placeholder components for routes we haven't built yet
const PlaceholderPage: React.FC<{ titleKey: string }> = ({ titleKey }) => {
  const { t } = useTranslation(['static', 'common']);
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{t(titleKey)}</h1>
        <p className="text-gray-600">{t('common.comingSoon', { ns: 'common' })}</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <TicketProvider>
        <Router>
          <div className="App">
            <Header />
            <main>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route 
                  path="/login" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <LoginPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/signup" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <SignupPage />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/verify-email"
                  element={
                    <ProtectedRoute allowUnverified>
                      <VerifyEmailPage />
                    </ProtectedRoute>
                  }
                />

                {/* Protected Routes */}
                <Route 
                  path="/upload" 
                  element={
                    <ProtectedRoute>
                      <TicketUploadPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/subscribe" 
                  element={
                    <ProtectedRoute>
                      <SubscribePage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/ticket/:appealSessionId/upload" 
                  element={
                    <ProtectedRoute>
                      <TicketUploadPage />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <Navigate to="/admin/cases" replace />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Navigate to="/admin/cases" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/cases"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminCasesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/cases/:caseId"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AdminCaseDetailPage />
                    </ProtectedRoute>
                  }
                />
                
                <Route 
                  path="/ticket/:id/review" 
                  element={
                    <ProtectedRoute>
                      <TicketReviewPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/ticket/:id/evidence" 
                  element={
                    <ProtectedRoute>
                      <TicketEvidencePage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/ticket/:id/reason" 
                  element={
                    <ProtectedRoute>
                      <PlaceholderPage titleKey="static.disputeReason" />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/ticket/:sessionId/appeal" 
                  element={
                    <ProtectedRoute>
                      <AppealSummaryPage />
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/ticket/:sessionId/filing"
                  element={
                    <ProtectedRoute>
                      <TicketFilingGuidePage />
                    </ProtectedRoute>
                  }
                />
                
                <Route 
                  path="/ticket/:id/submit" 
                  element={
                    <ProtectedRoute>
                      <PlaceholderPage titleKey="static.submissionGuidance" />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/ticket/:id/pay" 
                  element={
                    <ProtectedRoute>
                      <PlaceholderPage titleKey="static.payment" />
                    </ProtectedRoute>
                  } 
                />

                {/* Static Pages */}
                <Route path="/how-it-works" element={<PlaceholderPage titleKey="static.howItWorks" />} />
                <Route path="/pricing" element={<PlaceholderPage titleKey="static.pricing" />} />
                <Route path="/success-stories" element={<PlaceholderPage titleKey="static.successStories" />} />
                <Route path="/faq" element={<PlaceholderPage titleKey="static.faq" />} />
                <Route path="/contact" element={<PlaceholderPage titleKey="static.contact" />} />
                <Route path="/help" element={<PlaceholderPage titleKey="static.helpCenter" />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy-policy" element={<PlaceholderPage titleKey="static.privacyPolicy" />} />
                <Route path="/terms-of-service" element={<PlaceholderPage titleKey="static.termsOfService" />} />
                <Route path="/disclaimer" element={<PlaceholderPage titleKey="static.disclaimer" />} />

                {/* 404 Page */}
                <Route 
                  path="*" 
                  element={
                    <NotFound />
                  } 
                />
              </Routes>
            </main>
            <Footer />

            {/* Toast Notifications */}
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              className="z-50"
            />
          </div>
        </Router>
      </TicketProvider>
    </AuthProvider>
  );
}

const NotFound: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">{t('common.pageNotFound')}</h2>
        <p className="text-gray-600 mb-8">{t('common.notFoundMessage')}</p>
        <a href="/" className="btn-primary">
          {t('common.goHome')}
        </a>
      </div>
    </div>
  );
};

export default App;
